/* eslint-disable react/jsx-props-no-spreading */
import { useUrlGenerator, useRoutePush } from '@micromag/core/contexts';
import Editor from '@micromag/editor';
import set from 'lodash/set';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { validate } from 'uuid';
import { useParams, useSearch } from 'wouter';

import {
    useStory,
    useStoryVersionCreate,
    useStorySocket,
    useStoryLock,
    useStoryLockCreate,
} from '../../../hooks';
import useAutoSave from '../../../hooks/useAutoSave';

import { useApp } from '../../../contexts/AppContext';
import { useUser } from '../../../contexts/AuthContext';
import { useOrganisation } from '../../../contexts/OrganisationContext';
import MainLayout from '../../layouts/Main';
import ConfirmChangesModal from '../../modals/ConfirmChanges';
import EditorLocked from '../../modals/EditorLocked';
import EditorNavbar from '../../navbars/Editor';
import PromptModal from '../../partials/PromptModal';

const propTypes = {
    autoSaveDelay: PropTypes.number,
    className: PropTypes.string,
};

const defaultProps = {
    autoSaveDelay: 10,
    className: null,
};

const EditorPage = ({ autoSaveDelay, className }) => {
    const { id: userId = null } = useUser();
    const url = useUrlGenerator();
    const push = useRoutePush();
    const organisation = useOrganisation();
    const { branding = {}, namespaces = null } = organisation || {};

    const { memoryRouter } = useApp();
    const { story: storyId, path: screenPath } = useParams();
    const finalScreenPath = useMemo(() => {
        const parts = (screenPath || '').split('/');
        const [firstPart = null] = parts || [];
        return firstPart !== null && validate(firstPart) ? firstPart : null;
    }, [screenPath]);

    const search = useSearch();
    const { create: createQuery = false } = useMemo(
        () => (search !== null && search.length > 0 ? queryString.parse(search) : {}),
        [search],
    );
    const isCreateOpened = parseInt(createQuery, 10) === 1;

    const [saveLocked, setSaveLocked] = useState(false);
    const [, setSaveError] = useState(false);
    const { story = null } = useStory(storyId, { withoutCache: true });

    const { create: createStoryVersion, creating } = useStoryVersionCreate(storyId, setSaveError);
    const [promptOpen, setPromptOpened] = useState(false);
    const basePath = url('stories.editor', {
        story: storyId,
    });

    // eslint-disable-next-line no-unused-vars
    const { find: findStoryLock = null, finding = null } = useStoryLock(storyId);
    // eslint-disable-next-line no-unused-vars
    const { create: createStoryLock, creating: creatingLock = false } = useStoryLockCreate(storyId);

    const [editingAuthor, setEditingAuthor] = useState(null);
    const { id: editingAuthorId = null } = editingAuthor || {};

    const { last_lock: lastLock = null } = story || {};
    const { author: storyLockAuthor = null } = lastLock || {};
    const { id: storyLockAuthorId = null } = storyLockAuthor || {};

    const isCurrentEditor =
        storyLockAuthorId === null ||
        editingAuthorId === null ||
        (editingAuthorId !== null && parseInt(editingAuthorId, 10) === parseInt(userId, 10));

    const saveDisabled = promptOpen || !isCurrentEditor || story === null;

    // Updates from initial story
    useEffect(() => {
        setEditingAuthor(storyLockAuthor);
    }, [setEditingAuthor, storyLockAuthor]);

    const saveVersion = useCallback(
        (newStory = null) => {
            if (newStory !== null) {
                try {
                    // console.log(newStory);
                    createStoryVersion(newStory);
                } catch (e) {
                    // eslint-disable-next-line
                    console.log('failed save version', e);
                }
            }
        },
        [createStoryVersion],
    );

    const { storyValue, valueChanged, onManualSave, onChange, onUndo, canUndo, setValue } =
        useAutoSave(story, {
            delay: autoSaveDelay,
            onSave: saveVersion,
            disabled: creating || saveDisabled || saveLocked,
        });

    const onSocketMessage = useCallback(
        ({ event = null, data }) => {
            if (event === 'medias.updated') {
                const { medias = {} } = storyValue || {};
                const mediaPath = `media://${data.id}`;
                if (typeof medias[mediaPath] !== 'undefined') {
                    setValue({
                        ...storyValue,
                        medias: {
                            ...medias,
                            [mediaPath]: data,
                        },
                    });
                }
            }

            // Switch lock
            if (event === 'locks.updated') {
                const { author = null } = data || {};
                setEditingAuthor(author);
            }

            if (event === 'stories.diff') {
                const newValue = { ...storyValue };
                Object.keys(data).forEach((key) => {
                    set(newValue, key, data[key]);
                });
                setValue(newValue);
            }
        },
        [storyValue, setValue, storyValue, setEditingAuthor, userId],
    );
    useStorySocket(story, onSocketMessage);

    // Save prompt when missing changes
    const onPromptOpened = useCallback(() => {
        setPromptOpened(true);
    }, [setPromptOpened]);

    const onPromptClosed = useCallback(() => {
        setPromptOpened(false);
    }, [setPromptOpened]);

    // Actions on editor disabled
    const onKickAuthor = useCallback(() => {
        createStoryLock().then((response) => {
            const {
                document_id: documentId = null,
                last_document_version: lastDocumentVersion = null,
            } = response || {};
            setValue({ ...storyValue, ...lastDocumentVersion, id: documentId });
        });
    }, [url, push, storyId, setValue, storyValue]);

    const onLockedClosed = useCallback(() => {
        push('stories.show', {
            story: storyId,
        });
    }, [push, storyId]);

    // Disable save on focus/blur and get current lock
    const onFocus = useCallback(() => {
        setSaveLocked(false);
        // Fetch new lock
        findStoryLock()
            .then((res) => {
                const { author = null } = res || {};
                setEditingAuthor(author);
            })
            .catch(() => {
                setEditingAuthor(null);
            });
    }, [findStoryLock, setSaveLocked]);

    const onBlur = useCallback(() => {
        setSaveLocked(true);
    }, [setSaveLocked]);

    useEffect(() => {
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, [onFocus, onBlur]);

    return (
        <MainLayout
            fullscreen
            navbar={
                storyValue !== null ? (
                    <EditorNavbar
                        story={storyValue}
                        screenPath={finalScreenPath}
                        onClickSave={onManualSave}
                        onUndo={onUndo}
                        canUndo={canUndo}
                        saving={creating}
                        canSave={valueChanged && !saveDisabled}
                    />
                ) : null
            }
        >
            <div className={className}>
                {storyValue !== null ? (
                    <Editor
                        value={storyValue}
                        basePath={basePath}
                        memoryRouter={memoryRouter}
                        isCreateOpened={isCreateOpened}
                        viewerTheme={branding}
                        fullscreen
                        onChange={onChange}
                        screenNamespaces={namespaces}
                    />
                ) : null}
            </div>
            <PromptModal
                when={valueChanged}
                basePath={basePath}
                Modal={ConfirmChangesModal}
                modalProps={{ onSave: isCurrentEditor && !saveDisabled ? onManualSave : null }}
                onOpened={onPromptOpened}
                onClosed={onPromptClosed}
            />
            {!promptOpen && editingAuthor !== null && editingAuthorId !== userId ? (
                <EditorLocked
                    author={editingAuthor}
                    onConfirm={onKickAuthor}
                    onCancel={onLockedClosed}
                />
            ) : null}
        </MainLayout>
    );
};

EditorPage.propTypes = propTypes;
EditorPage.defaultProps = defaultProps;

export default EditorPage;
